import type { OHLCV } from '@shared/types/candle'

import { registerIndicator } from '@/indicators/registry'
import type { IndicatorConfig, IndicatorResult } from '@/types/indicator'
import { IndicatorBase } from '@/types/indicator'

/**
 * Simplified Market Profile.
 *
 * Computes a rolling TPO (Time Price Opportunity) histogram over the last
 * `lookback` bars.  Each bar that touches a price level adds one TPO count.
 * Outputs the three key levels as horizontal lines:
 *   POC  — Price level visited by the most bars (highest TPO count)
 *   VAH  — Value Area High: upper edge of the 70% TPO value area
 *   VAL  — Value Area Low:  lower edge of the 70% TPO value area
 *
 * Note: Full letter-by-letter TPO rendering requires custom canvas drawing which
 * is not supported by LWC's built-in series types.  The POC / VAH / VAL lines
 * capture the most actionable Market Profile information.
 */
const CONFIG: IndicatorConfig = {
  id: 'mktprofile',
  name: 'Market Profile',
  shortName: 'MP',
  category: 'professional',
  overlay: true,
  params: [
    { key: 'lookback', label: 'Lookback Bars', type: 'number', default: 200, min: 20, max: 1000 },
    { key: 'bins', label: 'Price Bins', type: 'number', default: 50, min: 10, max: 200 },
  ],
  lines: [
    { key: 'poc', label: 'POC', color: '#9c27b0', width: 2 },
    { key: 'vah', label: 'VAH', color: '#ba68c8', width: 1, style: 'dashed' },
    { key: 'val', label: 'VAL', color: '#7b1fa2', width: 1, style: 'dashed' },
  ],
}

interface MPResult {
  poc: number
  vah: number
  val: number
}

function computeMP(slice: OHLCV[], numBins: number): MPResult | null {
  if (slice.length === 0) return null

  let minPrice = Infinity
  let maxPrice = -Infinity
  for (const c of slice) {
    if (c.l < minPrice) minPrice = c.l
    if (c.h > maxPrice) maxPrice = c.h
  }
  if (maxPrice <= minPrice) return null

  const binSize = (maxPrice - minPrice) / numBins
  // TPO count: number of bars that touched each price bin
  const tpoAtBin = new Float64Array(numBins)

  for (const c of slice) {
    const binLow = Math.max(0, Math.floor((c.l - minPrice) / binSize))
    const binHigh = Math.min(numBins - 1, Math.floor((c.h - minPrice) / binSize))
    for (let b = binLow; b <= binHigh; b++) {
      tpoAtBin[b] += 1
    }
  }

  // Point of Control: bin with most TPO touches
  let pocBin = 0
  for (let b = 1; b < numBins; b++) {
    if (tpoAtBin[b] > tpoAtBin[pocBin]) pocBin = b
  }
  const poc = minPrice + (pocBin + 0.5) * binSize

  // Value Area: expand from POC until 70% of total TPO is captured
  const totalTPO = tpoAtBin.reduce((s, v) => s + v, 0)
  const target = totalTPO * 0.7
  let vahBin = pocBin
  let valBin = pocBin
  let areaTPO = tpoAtBin[pocBin]

  while (areaTPO < target) {
    const upTPO = vahBin + 1 < numBins ? tpoAtBin[vahBin + 1] : 0
    const downTPO = valBin - 1 >= 0 ? tpoAtBin[valBin - 1] : 0
    if (upTPO === 0 && downTPO === 0) break
    if (upTPO >= downTPO && vahBin + 1 < numBins) {
      vahBin++
      areaTPO += tpoAtBin[vahBin]
    } else if (valBin - 1 >= 0) {
      valBin--
      areaTPO += tpoAtBin[valBin]
    } else {
      vahBin++
      areaTPO += tpoAtBin[vahBin]
    }
  }

  return {
    poc,
    vah: minPrice + (vahBin + 1) * binSize,
    val: minPrice + valBin * binSize,
  }
}

class MarketProfile extends IndicatorBase {
  readonly config = CONFIG

  calculate(candles: OHLCV[], params: Record<string, number | string | boolean>): IndicatorResult {
    const lookback = Math.max(20, Number(params['lookback']) || 200)
    const numBins = Math.max(10, Number(params['bins']) || 50)
    const n = candles.length

    const poc: (number | null)[] = new Array<number | null>(n).fill(null)
    const vah: (number | null)[] = new Array<number | null>(n).fill(null)
    const val: (number | null)[] = new Array<number | null>(n).fill(null)

    for (let i = lookback - 1; i < n; i++) {
      const slice = candles.slice(i - lookback + 1, i + 1)
      const result = computeMP(slice, numBins)
      if (result) {
        poc[i] = result.poc
        vah[i] = result.vah
        val[i] = result.val
      }
    }

    return {
      lines: [
        { key: 'poc', values: poc },
        { key: 'vah', values: vah },
        { key: 'val', values: val },
      ],
    }
  }
}

registerIndicator(MarketProfile, CONFIG)
